import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowUpDown, ChevronDown } from "lucide-react";
import { cn } from "@/components/utils/utils";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { FilterConfig } from "@/components/ui/filter-dropdown";

export type SortMode = 'newest' | 'oldest' | 'upcoming' | 'customer' | 'budget-high' | 'budget-low';

interface SortTravelRequestButtonProps {
  sortMode: SortMode;
  onSortModeChange: (mode: SortMode) => void;
  travelRequests: TravelRequest[];
  onSortedTravelRequestsChange: (travelRequests: TravelRequest[]) => void;
  filters: FilterConfig[];
  onFilteredTravelRequestsChange: (travelRequests: TravelRequest[]) => void;
  disabled?: boolean;
}

export const SortTravelRequestButton: React.FC<SortTravelRequestButtonProps> = ({
  sortMode,
  onSortModeChange,
  travelRequests,
  onSortedTravelRequestsChange,
  filters,
  onFilteredTravelRequestsChange,
  disabled = false,
}) => {
  // Funzione di ordinamento
  const getSortedTravelRequests = (travelRequestsToSort: TravelRequest[], sortModeToApply: SortMode) => {
    const sortedTravelRequests = [...travelRequestsToSort];
    
    switch (sortModeToApply) {
      case 'newest':
        return sortedTravelRequests.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      case 'oldest':
        return sortedTravelRequests.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      case 'upcoming':
        return sortedTravelRequests.sort((a, b) => {
          const aDate = a.travel_start_date ? new Date(a.travel_start_date).getTime() : 0;
          const bDate = b.travel_start_date ? new Date(b.travel_start_date).getTime() : 0;
          return aDate - bDate;
        });
      case 'customer':
        return sortedTravelRequests.sort((a, b) => {
          const aName = `${a.contact_first_name} ${a.contact_last_name}`.toLowerCase();
          const bName = `${b.contact_first_name} ${b.contact_last_name}`.toLowerCase();
          return aName.localeCompare(bName);
        });
      case 'budget-high':
        return sortedTravelRequests.sort((a, b) => (b.budget_max || 0) - (a.budget_max || 0));
      case 'budget-low':
        return sortedTravelRequests.sort((a, b) => (a.budget_min || 0) - (b.budget_min || 0));
      default:
        return sortedTravelRequests;
    }
  };

  // Funzione di filtraggio (duplicata per mantenere la logica completa)
  const applyFilters = (travelRequestsToFilter: TravelRequest[], filtersToApply: FilterConfig[]) => {
    if (filtersToApply.length === 0) return travelRequestsToFilter;
    
    return travelRequestsToFilter.filter(travelRequest => {
      return filtersToApply.every(filter => {
        const { field, operation, value } = filter;
        
        switch (field) {
          case 'destinations': {
            const destinations = travelRequest.destinations.join(', ').toLowerCase();
            return operation === 'contains' ? destinations.includes(value.toLowerCase()) : false;
          }
          
          case 'customer_name': {
            const customerName = `${travelRequest.contact_first_name} ${travelRequest.contact_last_name}`.toLowerCase();
            return operation === 'contains' ? customerName.includes(value.toLowerCase()) : false;
          }
          
          case 'budget': {
            const budget = travelRequest.budget_max || 0;
            const filterValue = parseInt(value);
            switch (operation) {
              case 'equals': return budget === filterValue;
              case 'greater_than': return budget > filterValue;
              case 'less_than': return budget < filterValue;
              case 'greater_equal': return budget >= filterValue;
              case 'less_equal': return budget <= filterValue;
              default: return false;
            }
          }
          
          case 'guests': {
            const guests = travelRequest.total_guests || 0;
            const guestValue = parseInt(value);
            switch (operation) {
              case 'equals': return guests === guestValue;
              case 'greater_than': return guests > guestValue;
              case 'less_than': return guests < guestValue;
              case 'greater_equal': return guests >= guestValue;
              case 'less_equal': return guests <= guestValue;
              default: return false;
            }
          }
          
          case 'departure': {
            const departure = travelRequest.departure_point?.toLowerCase() || '';
            return operation === 'contains' ? departure.includes(value.toLowerCase()) : false;
          }
          
          case 'travel_start_date': {
            if (!filter.dateRange) return true;
            const travelRequestDate = travelRequest.travel_start_date ? new Date(travelRequest.travel_start_date).getTime() : 0;
            const fromDate = filter.dateRange.from ? new Date(filter.dateRange.from).getTime() : 0;
            const toDate = filter.dateRange.to ? new Date(filter.dateRange.to).getTime() : Number.MAX_SAFE_INTEGER;
            
            return travelRequestDate >= fromDate && travelRequestDate <= toDate;
          }
          
          case 'travel_end_date': {
            if (!filter.dateRange) return true;
            const travelRequestDate = travelRequest.travel_end_date ? new Date(travelRequest.travel_end_date).getTime() : 0;
            const fromDate = filter.dateRange.from ? new Date(filter.dateRange.from).getTime() : 0;
            const toDate = filter.dateRange.to ? new Date(filter.dateRange.to).getTime() : Number.MAX_SAFE_INTEGER;
            
            return travelRequestDate >= fromDate && travelRequestDate <= toDate;
          }
          
          case 'created_at': {
            if (!filter.dateRange) return true;
            const travelRequestDate = new Date(travelRequest.created_at).getTime();
            const fromDate = filter.dateRange.from ? new Date(filter.dateRange.from).getTime() : 0;
            const toDate = filter.dateRange.to ? new Date(filter.dateRange.to).getTime() : Number.MAX_SAFE_INTEGER;
            
            return travelRequestDate >= fromDate && travelRequestDate <= toDate;
          }
          
          default:
            return true;
        }
      });
    });
  };

  const handleSortModeChange = (mode: SortMode) => {
    onSortModeChange(mode);
    const sortedTravelRequests = getSortedTravelRequests(travelRequests, mode);
    onSortedTravelRequestsChange(sortedTravelRequests);
    
    // Applica anche i filtri esistenti ai dati ordinati
    const filteredAndSortedTravelRequests = applyFilters(sortedTravelRequests, filters);
    onFilteredTravelRequestsChange(filteredAndSortedTravelRequests);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="flex items-center gap-1 sm:gap-2"
          disabled={disabled}
        >
          <ArrowUpDown className="h-4 w-4" />
          <span className="hidden sm:inline">Sort</span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => handleSortModeChange('newest')}
          className={cn(sortMode === 'newest' && "bg-accent")}
        >
          Newest first
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleSortModeChange('oldest')}
          className={cn(sortMode === 'oldest' && "bg-accent")}
        >
          Oldest first
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleSortModeChange('upcoming')}
          className={cn(sortMode === 'upcoming' && "bg-accent")}
        >
          Upcoming first
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleSortModeChange('customer')}
          className={cn(sortMode === 'customer' && "bg-accent")}
        >
          Customer name (A–Z)
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleSortModeChange('budget-high')}
          className={cn(sortMode === 'budget-high' && "bg-accent")}
        >
          Budget (highest first)
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleSortModeChange('budget-low')}
          className={cn(sortMode === 'budget-low' && "bg-accent")}
        >
          Budget (lowest first)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
